import "swiper/css";
import "swiper/css/mousewheel";
import "swiper/css/parallax";
import "swiper/css/navigation";

import { Box, Grid, IconButton, styled } from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, {
  Keyboard,
  Mousewheel,
  Navigation,
  Pagination,
  Parallax,
} from "swiper";
import { useRef, useState } from "react";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { PRODUCTS } from "./constants";
import ProductDescription from "./components/ProductDescription";
import ProductItem from "./components/ProductItem";
import useIsMobileView from "hooks/useIsMobileView";
import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import dafiLogo from "assets/logo/logo-w.png";

function Products() {
  const [searchParams] = useSearchParams();

  const isMobileView = useIsMobileView();
  const targetSectionRef = useRef(null);

  const [slideProgress, setSlideProgress] = useState(0);
  const [transitionToggle, setTransitionToggle] = useState(false);
  const [pageMove, setPageMove] = useState([0, 0]);
  const [selectedProduct, setSelectedProduct] = useState(PRODUCTS[0]);

  const handleOnProgress = (_, progress) => {
    const progressPercentage = +progress * 100;

    setSlideProgress(progressPercentage);
  };

  const handleTransitionStart = (_) => {
    setTransitionToggle(true);
  };

  const handleTransitionEnd = (e) => {
    const currentProduct = PRODUCTS?.[e?.activeIndex];

    setSelectedProduct(currentProduct);
    setTransitionToggle(false);
  };

  const rotation = useMemo(() => {
    const max = 270;
    const rotatePercentage = (max * slideProgress) / 100;

    return -rotatePercentage;
  }, [slideProgress]);

  const handleMouseMove = (e) => {
    const { pageX, pageY } = e;
    const { innerWidth, innerHeight } = window;

    const moveX = (pageX / innerWidth) * 8;
    const moveY = (pageY / innerHeight) * 8;

    const limitedMoveX = Math.min(60, Math.max(-60, moveX)).toFixed(2);
    const limitedMoveY = Math.min(60, Math.max(-60, moveY)).toFixed(2);

    setPageMove([limitedMoveX, limitedMoveY]);
  };

  const scrollToSection = () => {
    if (targetSectionRef.current) {
      targetSectionRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const onSwiper = (e) => {
    const currentProduct = +searchParams.get("product") || 0;

    e.slideTo(currentProduct);
  };

  SwiperCore.use([Navigation, Mousewheel, Keyboard, Pagination, Parallax]);

  return (
    <PageWrapper>
      <Grid container justifyContent="stretch">
        <Grid item xs={12} md={6} sx={{ position: 'relative' }}>
          <Header component={Link} to="/lipbalm/">
            <img src={dafiLogo} alt="Dafi Logo" />
          </Header>
          <SwiperWrapper onMouseMove={handleMouseMove}>
            {isMobileView && (
              <ScrollToNextSection onClick={scrollToSection} size="large">
                <ExpandLessIcon />
              </ScrollToNextSection>
            )}

            <BackgroundAnimation
              colors={selectedProduct?.colors}
              rotation={rotation}
            />

            <SwiperStyle
              onTouchStart={handleTransitionStart}
              onProgress={handleOnProgress}
              onTransitionStart={handleTransitionStart}
              onTransitionEnd={handleTransitionEnd}
              navigation
              onSwiper={onSwiper}
              onSlideChangeTransitionEnd={handleTransitionEnd}
              scrollbar={{ draggable: true }}
              mousewheel={true}
              transitionToggle={transitionToggle}
            >
              {PRODUCTS.map((product, index) => (
                <ProductWrapper key={index}>
                  <ProductItem
                    selectedProduct={selectedProduct}
                    transitionToggle={transitionToggle}
                    pageMove={pageMove}
                    product={product}
                    rotation={rotation}
                    slideProgress={slideProgress}
                  />
                </ProductWrapper>
              ))}
            </SwiperStyle>
          </SwiperWrapper>
        </Grid>

        <Grid item xs={12} md={6} ref={targetSectionRef}>
          <ProductDescription selectedProduct={selectedProduct} />
        </Grid>
      </Grid>
    </PageWrapper>
  );
}

const ScrollToNextSection = styled(IconButton)(() => ({
  position: "absolute",
  bottom: 20,
  left: "50%",
  transform: "translateX(-50%)",
  color: "#ffffff",
  zIndex: 99,
}));

const SwiperStyle = styled(Swiper)(({ theme, transitionToggle }) => ({
  overflow: transitionToggle ? "hidden" : "initial !important",

  ".swiper-button-prev, .swiper-button-next": {
    color: "#ffffff",

    "&:after": {
      fontSize: "26px",
    },
  },

  [theme.breakpoints.down("md")]: {
    ".swiper-button-prev, .swiper-button-next": {
      background: "rgba(255, 255, 255, 0.14)",
      boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
      border: "1px solid rgba(255, 255, 255, 0.2)",
      backdropFilter: "blur(8.5px)",
      borderRadius: "50%",
      width: "60px",
      height: "60px",
    },

    ".swiper-button-prev": {
      left: "-30px",
      paddingLeft: "20px",
    },

    ".swiper-button-next": {
      right: "-30px",
      paddingRight: "20px",
    },
  },
}));

const PageWrapper = styled(Box)(() => ({
  overflow: "hidden",

}));

const Header = styled(Box)(({ theme }) => ({
  img: {
    position: "absolute",
    top: 20,
    left: '50%',
    transform: 'translateX(-50%)',
    width: "120px",
    objectFit: "contain",
    zIndex: 4,

    [theme.breakpoints.up('md')]: {
      left: 20,
      transform: 'initial',
    }
  },
}));

const ProductWrapper = styled(SwiperSlide)(() => ({
  width: "100%",
  height: "var(--app-height)",
  overflow: "initial !important",
  transform: "scale(0) !important",
  opacity: 0,
  transition: "transform 0.3s, opacity 0.1s",

  "&.swiper-slide-active": {
    transform: "scale(1) !important",
    opacity: 1,
    transition: "transform 0.3s , opacity 0.7s",
  },
}));

const BackgroundAnimation = styled(Box)(({ theme, colors, rotation }) => ({
  position: "fixed",
  width: "100%",
  height: "100%",
  left: "50%",
  top: "50%",
  transform: `translate(-50%, -50%)`,
  background: colors?.secondary,
  transition: "0.8s background",
}));

const SwiperWrapper = styled(Box)(({ theme }) => ({
  position: "relative",

  "&:before": {
    content: "''",
    position: "absolute",
    width: "100%",
    left: 0,
    top: 0,
    height: "100%",
    background:
      "radial-gradient(circle, rgba(10,62,86,0) 50%, rgb(0 0 0 / 35%) 100%)",
    transition: "all 1s",
    zIndex: 1,

    [theme.breakpoints.down('sm')]: {
      background:
        "radial-gradient(circle, rgba(10,62,86,0) 50%, rgb(0 0 0 / 54%) 100%)",
    }
  },
}));

export default Products;
